import {computed, reactive} from "vue";

import trans from "helpers/_translation";
import { isMobile } from "helpers/_device";

export const statisticsData = reactive({
	date: new Date(),
	date_range: "month",

	date_start: null, // Used for overruling default behaviour
	date_end: null // Used for overruling default behaviour
});

export const resolveStartDate = (date) => {
	// statisticsData date minus date_range
	let startDate = new Date(date); // Clone the date
	switch (statisticsData.date_range) {
		case "day":
			startDate.setDate(startDate.getDate() - 1);
			break;
		case "week":
			startDate.setDate(startDate.getDate() - 7);
			break;
		case "month":
			startDate.setMonth(startDate.getMonth() - 1);
			break;
		case "year":
			startDate.setFullYear(startDate.getFullYear() - 1);
			break;
		default:
			console.error("Unknown date range", statisticsData.date_range);
	}
	return startDate;
};

export const setStartDate = (date) => {
	// Set the start date
	statisticsData.date_start = new Date(date);
};
export const setEndDate = (date) => {
	// Set the end date
	statisticsData.date_end = new Date(date);
};

export const endDate = computed(() => {
	// The date we calculate with
	return statisticsData.date_end ?? statisticsData.date;
});
export const startDate = computed(() => {
	// Based on range defined use endDate minus the range
	return statisticsData.date_start ?? resolveStartDate(endDate.value);
});

export const endOffset = computed(() => {
	// Use the current range start date as offset end date
	return new Date(startDate.value);
});
export const startOffset = computed(() => {
	// Based on range defined use the endOffset minus the range
	return resolveStartDate(endOffset.value);
});




export const columnType = computed(() => {
	const start = new Date(startDate.value);
	const end = new Date(endDate.value);

	// Calculate the differences
	const yearDiff = end.getFullYear() - start.getFullYear();
	const monthDiff = end.getMonth() - start.getMonth() + (yearDiff * 12); // Total months difference
	const dayDiff = (end - start) / (1000 * 60 * 60 * 24); // Total days difference

	// Determine the column type based on the differences
	if (yearDiff >= 2) {
		return "year";
	} else if (monthDiff >= 3) {
		return "month";
	} else if (dayDiff >= 14) {
		return "week";
	} else {
		return "day";
	}
});
export const columnItems = computed(() => {
	const start = new Date(startDate.value);
	const end = new Date(endDate.value);
	end.setDate(end.getDate() + 1); // Add one day to include the end date

	// Helper function to add months to a date
	const addMonths = (date, months) => {
		const newDate = new Date(date);
		newDate.setMonth(newDate.getMonth() + months);
		return newDate;
	};

	// Helper function to add days to a date
	const addDays = (date, days) => {
		const newDate = new Date(date);
		newDate.setDate(newDate.getDate() + days);
		return newDate;
	};

	// Helper function to adjust a date to the nearest Monday
	const adjustToMonday = (date) => {
		const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
		const diff = day === 0 ? -6 : 1 - day; // If it's Sunday, adjust to last Monday; else adjust to current week's Monday
		date.setDate(date.getDate() + diff);
		return date;
	};

	// Helper function to adjust a date to the nearest Sunday
	const adjustToSunday = (date) => {
		const day = date.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
		const diff = day === 0 ? 0 : 7 - day; // If it's Sunday, return Sunday; otherwise, adjust to next Sunday
		date.setDate(date.getDate() + diff);
		return date;
	};

	// Function to generate column items based on the column type
	let items = [];

	if (columnType.value === "year") {
		// "year": Generate full years
		let currentStart = new Date(start);
		currentStart = adjustToMonday(currentStart); // Ensure start is on Monday
		let currentEnd = addMonths(currentStart, 12);
		currentEnd = adjustToSunday(currentEnd); // Ensure end is on Sunday
		while (currentEnd <= end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(currentEnd),
			});
			currentStart = new Date(currentEnd);
			currentEnd = addMonths(currentStart, 12);
			currentEnd = adjustToSunday(currentEnd); // Adjust end to Sunday
		}
		if (currentStart < end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(end),
			});
		}
	} else if (columnType.value === "month") {
		// "month": Generate full months
		let currentStart = new Date(start);
		currentStart = adjustToMonday(currentStart); // Ensure start is on Monday
		let currentEnd = addMonths(currentStart, 1);
		currentEnd = adjustToSunday(currentEnd); // Ensure end is on Sunday
		while (currentEnd <= end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(currentEnd),
			});
			currentStart = new Date(currentEnd);
			currentEnd = addMonths(currentStart, 1);
			currentEnd = adjustToSunday(currentEnd); // Adjust end to Sunday
		}
		if (currentStart < end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(end),
			});
		}
	} else if (columnType.value === "week") {
		// "week": Generate full weeks
		let currentStart = new Date(start);
		currentStart = adjustToMonday(currentStart); // Ensure start is on Monday
		let currentEnd = addDays(currentStart, 6); // Full week (7 days), ending on Sunday
		while (currentEnd <= end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(currentEnd),
			});
			currentStart = addDays(currentEnd, 1); // Move to next Monday
			currentEnd = addDays(currentStart, 6); // Full week (7 days), ending on Sunday
		}
		if (currentStart < end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(end),
			});
		}
	} else {
		// "day": Generate single days
		let currentStart = new Date(start);
		let currentEnd = addDays(currentStart, 1); // Single day
		while (currentEnd <= end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(currentEnd),
			});
			currentStart = new Date(currentEnd);
			currentEnd = addDays(currentStart, 1);
		}
		if (currentStart < end) {
			items.push({
				start: new Date(currentStart),
				end: new Date(end),
			});
		}
	}

	return items;
});


// Helper function to get the week number of the year based on a given date
export const getWeekNumber = (date) => {
	const tempDate = new Date(date);
	tempDate.setHours(0, 0, 0, 0);

	// Set the date to the nearest Thursday (ISO 8601)
	tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7));

	// Get the first day of the year
	const yearStart = new Date(tempDate.getFullYear(), 0, 1);

	// Calculate the number of days between the current date and the first day of the year
	const dayDiff = (tempDate - yearStart) / (1000 * 60 * 60 * 24);

	// Calculate the week number
	const weekNumber = Math.ceil((dayDiff + 1) / 7);

	return weekNumber;
};

const monthNames = ["jan.", "feb.", "mrt.", "apr.", "mei", "Jun.", "jul.", "aug.", "sep.", "oct.", "nov.", "dec."];
export const formatDate = (date, show_year = true) => {
	const day = date.getDate();
	const month = trans(monthNames[date.getMonth()]);
	const year = date.getFullYear();
	if (!show_year) {
		return `${day} ${month}`;
	}
	return `${day} ${month} ${year}`;
};

export const columnTitles = computed(() => {
	// Titles in Dutch
	return columnItems.value.map(item => {
		if (columnType.value === "year") {
			// For "year", return the year from the start date
			return item.start.getFullYear().toString();
		} else if (columnType.value === "month") {
			// For "month", return the month abbreviation (e.g., Dec. 2023)
			const month = monthNames[item.start.getMonth()];
			const year = item.start.getFullYear();
			return `${month} ${year}`;
		} else if (columnType.value === "week") {
			// For "week" use the weeknumber of the year
			// (e.g., "Week 23")
			// return `Week ${getWeekNumber(item.start)}`;

			const WEEK = `Week ${getWeekNumber(item.start)}`;

			// For "week", return the week range (e.g., "18 Nov. - 24 Nov. 2024")
			if (isMobile) {
				return [WEEK];
			}
			if (item.start.getFullYear() === item.end.getFullYear()) {
				return [WEEK, `(${formatDate(item.start, false)} - ${formatDate(item.end)})`];
			}
			return [WEEK, `(${formatDate(item.start)} - ${formatDate(item.end)})`];
		} else if (columnType.value === "day") {
			// For "day", return the day in the format (e.g., "18 Nov. 2024")
			return formatDate(item.start);
		}
	});
});


export const date_string = (value) => {
	return computed(() => {
		return `${value.toJSON().slice(0,10)} ${value.toJSON().slice(11,19)}`;
	}).value;
};

export const date_filters = (key = "creation_date") => {
	return computed(() => {
		const tmpStartDate = new Date(startDate.value);
		tmpStartDate.setHours(0, 0, 0, 0);
		tmpStartDate.setMinutes(tmpStartDate.getMinutes() - tmpStartDate.getTimezoneOffset());
		const tmpEndDate = new Date(endDate.value);
		tmpEndDate.setDate(tmpEndDate.getDate() + 1);
		tmpEndDate.setHours(0, 0, 0, 0);
		tmpEndDate.setMinutes(tmpEndDate.getMinutes() - tmpEndDate.getTimezoneOffset());
		const filters = [
			[
				"FieldFilter",
				{
					"c": key,
					"o": ">=",
					"v": date_string(tmpStartDate)
				}
			],
			[
				"FieldFilter",
				{
					"c": key,
					"o": "<",
					"v": date_string(tmpEndDate)
				}
			]
		];
		return filters;
	}).value;
}